import { X } from 'lucide-react';
import { useEffect } from 'react';

const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.webm', '.m4v'];
const AUDIO_EXTENSIONS = ['.mp3', '.wav', '.m4a', '.aac', '.flac', '.ogg'];

type PreviewKind = 'image' | 'video' | 'audio' | 'file';

/** What the key's extension says the file is — the same three lists the
 * thumbnail and the Content type column use, so a row that says "Video"
 * opens as a video. */
function previewKind(mediaKey: string): PreviewKind {
  const lower = mediaKey.toLowerCase();
  if (VIDEO_EXTENSIONS.some((ext) => lower.endsWith(ext))) return 'video';
  if (AUDIO_EXTENSIONS.some((ext) => lower.endsWith(ext))) return 'audio';
  if (/\.(png|jpe?g|webp|gif|avif)$/.test(lower)) return 'image';
  return 'file';
}

interface MediaPreviewDialogProps {
  mediaKey: string;
  /** The signed URL the caller already holds for this key (from the ONE
   * media-sign call per page); null = signing refused/failed. */
  url: string | null;
  onClose: () => void;
}

/**
 * Full-size preview of one piece of a user's content (ClickUp 869f4c9g1),
 * opened by clicking a thumbnail. SUPERADMIN ONLY — the caller renders the
 * thumbnail as a button only when `canOpenUserMedia(role)`, and the URL it
 * hands in came from POST /api/admin/v2/media/sign, which refuses every
 * other role. Same plain-modal shape as ConfirmDialog (no portal library
 * here); Escape and the backdrop close it.
 */
export function MediaPreviewDialog({ mediaKey, url, onClose }: MediaPreviewDialogProps) {
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const kind = previewKind(mediaKey);
  const fileName = mediaKey.split('/').pop() ?? mediaKey;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Preview ${fileName}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4"
      onClick={onClose}
      data-testid="media-preview-dialog"
    >
      <div
        className="flex max-h-full w-full max-w-4xl flex-col overflow-hidden rounded-card border border-border bg-surface-field"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-2">
          <span className="truncate text-meta text-fg-secondary" title={mediaKey}>
            {fileName}
          </span>
          <div className="flex shrink-0 items-center gap-2">
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="text-meta font-medium text-primary hover:underline"
              >
                Open original
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close preview"
              className="rounded-card p-1 text-fg-secondary hover:bg-state-hover"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="flex min-h-0 flex-1 items-center justify-center bg-surface-muted p-2">
          {url === null && (
            <p className="text-body text-fg-hint">Preview unavailable for this file.</p>
          )}
          {url && kind === 'image' && (
            <img
              src={url}
              alt={fileName}
              className="max-h-[80vh] max-w-full object-contain"
              data-testid="media-preview-image"
            />
          )}
          {url && kind === 'video' && (
            <video
              src={url}
              controls
              autoPlay
              className="max-h-[80vh] max-w-full"
              data-testid="media-preview-video"
            />
          )}
          {url && kind === 'audio' && (
            <audio src={url} controls className="w-full" data-testid="media-preview-audio" />
          )}
          {url && kind === 'file' && (
            <p className="text-body text-fg-secondary">
              No inline preview for this type — use “Open original”.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
